import { fmtFecha } from '../../lib/utils';
import { useState, useMemo } from 'react';
import { comprasApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RefreshCw, Search, CheckCircle, XCircle, Clock, Printer, Package, X, FileText, Wrench, ShoppingCart, Receipt } from 'lucide-react';
import { OCNumber } from '../../lib/ocFormat';

const printStyles = `
@media print {
  body * { visibility: hidden; }
  #oc-print-content, #oc-print-content * { visibility: visible; }
  #oc-print-content { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
  .print-hide { display: none !important; }
}
`;

const TABS = [
  { key: 'todos', label: 'Todos', icon: Package, color: 'blue' },
  { key: 'material', label: 'Materiales', icon: ShoppingCart, color: 'emerald' },
  { key: 'servicio', label: 'Servicios', icon: Wrench, color: 'orange' },
  { key: 'gasto_general', label: 'Gastos Generales', icon: Receipt, color: 'purple' },
  { key: 'sin_clasificar', label: 'Sin Clasificar', icon: FileText, color: 'gray' },
];

export const EstadoComprasOT = () => {
  const [otNumero, setOtNumero] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('todos');
  const [selectedOC, setSelectedOC] = useState(null);
  const [detalleOC, setDetalleOC] = useState(null);
  const [loadingDetalleOC, setLoadingDetalleOC] = useState(false);

  const handleBuscar = async () => {
    if (!otNumero || isNaN(parseInt(otNumero))) {
      setError('Ingrese un numero de OT valido');
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    setActiveTab('todos');
    try {
      const res = await comprasApi.getEstadoCompletoPorOT(parseInt(otNumero));
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleBuscar();
  };

  const filteredDetalles = useMemo(() => {
    if (!data?.detalles) return [];
    if (activeTab === 'todos') return data.detalles;
    return data.detalles.filter(d => d.tipo === activeTab);
  }, [data, activeTab]);

  const filteredResumen = useMemo(() => {
    if (!filteredDetalles.length) return { total_items: 0, con_oc_aprobada: 0, con_oc_gestionada: 0, con_recepcion: 0, costo_total_neto: 0 };
    return {
      total_items: filteredDetalles.length,
      con_oc_aprobada: filteredDetalles.filter(r => r.oc_aprobada).length,
      con_oc_gestionada: filteredDetalles.filter(r => r.oc_gestionada).length,
      con_recepcion: filteredDetalles.filter(r => r.tiene_recepcion).length,
      costo_total_neto: filteredDetalles.filter(r => r.oc_aprobada).reduce((s, r) => s + (r.total_linea || 0), 0),
    };
  }, [filteredDetalles]);

  const tabCounts = useMemo(() => {
    if (!data?.detalles) return {};
    const counts = {};
    for (const d of data.detalles) {
      counts[d.tipo] = (counts[d.tipo] || 0) + 1;
    }
    counts.todos = data.detalles.length;
    return counts;
  }, [data]);

  const handleVerDetalleOC = async (numeroOc) => {
    setLoadingDetalleOC(true);
    setSelectedOC(numeroOc);
    try {
      const res = await comprasApi.getOrdenCompraDetalle(numeroOc);
      setDetalleOC(res.data);
    } catch (_err) {
      setDetalleOC(null);
    } finally {
      setLoadingDetalleOC(false);
    }
  };

  const closeDetalleOC = () => { setSelectedOC(null); setDetalleOC(null); };
  const handlePrintOC = () => window.print();

  const fmtCant = (num) => {
    if (num === null || num === undefined) return '0';
    return Number.isInteger(num) ? num.toLocaleString('es-CL') : num.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };
  const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');
  const handlePrint = () => window.print();

  const StatusIcon = ({ value }) => {
    if (value === true || value === 'Si') return <CheckCircle className="w-4 h-4 text-green-600 inline" />;
    if (value === false || value === 'No' || value === 'Pendiente') return <XCircle className="w-4 h-4 text-red-500 inline" />;
    if (value === 'Parcial') return <Clock className="w-4 h-4 text-orange-500 inline" />;
    return <span className="text-gray-400">-</span>;
  };

  const tipoLabel = (tipo) => {
    const map = { material: 'Material', servicio: 'Servicio', gasto_general: 'Gasto', sin_clasificar: 'S/C' };
    return map[tipo] || tipo;
  };

  const tipoBadgeClass = (tipo) => {
    const map = {
      material: 'bg-emerald-100 text-emerald-700',
      servicio: 'bg-orange-100 text-orange-700',
      gasto_general: 'bg-purple-100 text-purple-700',
      sin_clasificar: 'bg-gray-100 text-gray-600',
    };
    return map[tipo] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="p-4" data-testid="estado-compras-ot-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 print:hidden">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Estado de Compras por OT
          </h1>
          <p className="text-sm text-muted-foreground">
            Ingrese una OT para ver el estado de pedidos, ordenes de compra y recepciones
          </p>
        </div>
      </div>

      {/* Search Box */}
      <div className="bg-white border rounded-lg p-6 mb-6 print:hidden">
        <div className="flex gap-4 items-end max-w-md">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Numero de OT</label>
            <Input
              type="number"
              placeholder="Ej: 3457"
              value={otNumero}
              onChange={(e) => setOtNumero(e.target.value)}
              onKeyPress={handleKeyPress}
              className="text-lg"
              data-testid="ot-input"
            />
          </div>
          <Button onClick={handleBuscar} disabled={loading} className="h-10" data-testid="buscar-btn">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            Buscar
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4" data-testid="error-message">{error}</div>
      )}

      {data && (
        <>
          {/* Print Header */}
          <div className="hidden print:block text-center mb-4 border-b-2 border-black pb-2">
            <h1 className="text-lg font-bold">ESTADO DE COMPRAS - OT {data.ot_numero}</h1>
            <p className="text-sm">{data.descripcion_ot}</p>
          </div>

          {/* OT Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-lg font-bold text-blue-800" data-testid="ot-title">OT {data.ot_numero}</h2>
                <p className="text-blue-700">{data.descripcion_ot || 'Sin descripcion'}</p>
              </div>
              <Button onClick={handlePrint} variant="outline" size="sm" className="print:hidden" data-testid="print-btn">
                <Printer className="w-4 h-4 mr-1" /> Imprimir
              </Button>
            </div>
          </div>

          {/* Cost Breakdown by Type */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 print:grid-cols-4">
            <div className="bg-white border-2 border-blue-200 rounded p-3 text-center">
              <p className="text-xs text-muted-foreground">Costo Total Neto</p>
              <p className="text-lg font-bold text-blue-700" data-testid="costo-total">${fmt(data.resumen?.costo_total_neto)}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
              <p className="text-xs text-emerald-700 flex items-center justify-center gap-1"><ShoppingCart className="w-3 h-3" /> Materiales</p>
              <p className="text-lg font-bold text-emerald-700" data-testid="costo-materiales">${fmt(data.resumen?.costo_materiales)}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded p-3 text-center">
              <p className="text-xs text-orange-700 flex items-center justify-center gap-1"><Wrench className="w-3 h-3" /> Servicios</p>
              <p className="text-lg font-bold text-orange-700" data-testid="costo-servicios">${fmt(data.resumen?.costo_servicios)}</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded p-3 text-center">
              <p className="text-xs text-purple-700 flex items-center justify-center gap-1"><Receipt className="w-3 h-3" /> Gastos Grales.</p>
              <p className="text-lg font-bold text-purple-700" data-testid="costo-gastos">${fmt(data.resumen?.costo_gastos)}</p>
            </div>
          </div>

          {/* Status Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 print:grid-cols-4">
            <div className="bg-white border rounded p-3 text-center">
              <p className="text-xs text-muted-foreground">Total Items</p>
              <p className="text-xl font-bold" data-testid="total-items">{filteredResumen.total_items}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-3 text-center">
              <p className="text-xs text-green-700">Con OC Aprobada</p>
              <p className="text-xl font-bold text-green-700" data-testid="con-oc-aprobada">{filteredResumen.con_oc_aprobada}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
              <p className="text-xs text-blue-700">Con OC Gestionada</p>
              <p className="text-xl font-bold text-blue-700" data-testid="con-oc-gestionada">{filteredResumen.con_oc_gestionada}</p>
            </div>
            <div className="bg-teal-50 border border-teal-200 rounded p-3 text-center">
              <p className="text-xs text-teal-700">Con Recepcion</p>
              <p className="text-xl font-bold text-teal-700" data-testid="con-recepcion">{filteredResumen.con_recepcion}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 overflow-x-auto print:hidden" data-testid="type-tabs">
            {TABS.map(tab => {
              const count = tabCounts[tab.key] || 0;
              if (tab.key !== 'todos' && !count) return null;
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`tab-${tab.key}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-xs font-medium border border-b-0 whitespace-nowrap transition-colors ${
                    isActive
                      ? `bg-white border-gray-300 text-${tab.color}-700 shadow-sm`
                      : 'bg-gray-50 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${isActive ? `bg-${tab.color}-100 text-${tab.color}-700` : 'bg-gray-200 text-gray-600'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Filtered Total */}
          {activeTab !== 'todos' && (
            <div className="bg-gray-100 border rounded p-2 mb-3 text-center text-sm">
              <span className="text-muted-foreground">Total Neto ({TABS.find(t => t.key === activeTab)?.label}): </span>
              <span className="font-bold text-green-700" data-testid="filtered-total">${fmt(filteredResumen.costo_total_neto)}</span>
            </div>
          )}

          {/* Detail Table */}
          {filteredDetalles.length > 0 ? (
            <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
              <table className="w-full text-xs border-collapse" data-testid="detalles-table">
                <thead className="sticky top-14 lg:top-0 z-20">
                  <tr className="bg-gray-800 text-white">
                    <th className="border border-gray-600 p-1 text-left w-16">N. OC</th>
                    <th className="border border-gray-600 p-1 text-center w-14">Tipo</th>
                    <th className="border border-gray-600 p-1 text-center w-16">Cantidad</th>
                    <th className="border border-gray-600 p-1 text-left">Descripcion</th>
                    <th className="border border-gray-600 p-1 text-right w-24">V. Unitario</th>
                    <th className="border border-gray-600 p-1 text-right w-24">Total Neto</th>
                    <th className="border border-gray-600 p-1 text-center w-20">Pedido VB</th>
                    <th className="border border-gray-600 p-1 text-center w-20">OC Aprobada</th>
                    <th className="border border-gray-600 p-1 text-center w-20">Recepcion</th>
                    <th className="border border-gray-600 p-1 text-center w-24">N. Factura</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetalles.map((det, idx) => (
                    <tr
                      key={idx}
                      className={`cursor-pointer hover:bg-blue-100 transition-colors ${
                        det.tiene_recepcion ? 'bg-teal-50' :
                        det.oc_aprobada ? 'bg-green-50' :
                        (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50')
                      }`}
                      onClick={() => handleVerDetalleOC(det.numero_oc)}
                      title="Click para ver detalle de la OC"
                      data-testid={`oc-row-${det.numero_oc}-${idx}`}
                    >
                      <td className="border p-1 font-mono text-blue-600 underline"><OCNumber oc={det} size="sm" /></td>
                      <td className="border p-1 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${tipoBadgeClass(det.tipo)}`}>
                          {tipoLabel(det.tipo)}
                        </span>
                      </td>
                      <td className="border p-1 text-center">{fmtCant(det.cantidad)}</td>
                      <td className="border p-1 truncate max-w-[200px]" title={det.descripcion}>{det.descripcion}</td>
                      <td className="border p-1 text-right font-mono">${fmt(det.precio_unitario)}</td>
                      <td className="border p-1 text-right font-mono">${fmt(det.total_linea)}</td>
                      <td className="border p-1 text-center">
                        <StatusIcon value={det.pedido_vb} />
                        <span className="ml-1 text-[10px]">{det.pedido_vb ? 'Si' : 'No'}</span>
                      </td>
                      <td className="border p-1 text-center">
                        <StatusIcon value={det.oc_aprobada} />
                        <span className="ml-1 text-[10px]">{det.oc_aprobada ? 'Si' : 'No'}</span>
                      </td>
                      <td className="border p-1 text-center">
                        <StatusIcon value={det.tiene_recepcion} />
                        <span className="ml-1 text-[10px]">{det.tiene_recepcion ? 'Si' : 'No'}</span>
                      </td>
                      <td className="border p-1 text-center font-mono text-[10px]">
                        {det.factura_recepcion ? (
                          <span className="text-teal-700">{det.factura_recepcion}</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-800 text-white font-bold">
                    <td colSpan={5} className="border border-gray-600 p-1 text-right">TOTAL:</td>
                    <td className="border border-gray-600 p-1 text-right font-mono" data-testid="table-total">
                      ${fmt(filteredResumen.costo_total_neto)}
                    </td>
                    <td colSpan={4} className="border border-gray-600 p-1"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p className="text-muted-foreground">No hay ordenes de compra para esta seleccion</p>
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground print:hidden">
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Completado</span>
            <span className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-500" /> Pendiente</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-orange-500" /> Parcial</span>
            <span className="border-l pl-4 flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span> Material</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-orange-100 border border-orange-300"></span> Servicio</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-purple-100 border border-purple-300"></span> Gasto Gral.</span>
          </div>
          <div className="mt-2 text-xs text-blue-600 print:hidden">
            <FileText className="w-3 h-3 inline mr-1" />
            Haga click en una fila para ver el detalle completo de la Orden de Compra
          </div>
        </>
      )}

      {/* Modal Detalle OC */}
      {selectedOC && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center print:bg-white print:static" data-testid="oc-detail-modal">
          <style>{printStyles}</style>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden mx-4 print:shadow-none print:max-h-none print:mx-0">
            <div className="flex justify-between items-center p-3 border-b bg-blue-100 print:bg-white print:border-b-2 print:border-black">
              <h3 className="font-bold text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 print:hidden" />
                Detalle Orden de Compra N. {selectedOC}
              </h3>
              <div className="flex gap-2 print-hide">
                <Button variant="outline" size="sm" onClick={handlePrintOC} data-testid="print-oc-modal">
                  <Printer className="w-4 h-4 mr-1" /> Imprimir
                </Button>
                <Button variant="ghost" size="sm" onClick={closeDetalleOC} data-testid="close-oc-modal">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div id="oc-print-content" className="p-4 overflow-auto max-h-[calc(85vh-60px)] print:max-h-none print:overflow-visible">
              {loadingDetalleOC ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">Cargando detalle...</p>
                </div>
              ) : detalleOC ? (
                <>
                  <div className="hidden print:block text-center mb-4 pb-2 border-b-2 border-black">
                    <h1 className="text-lg font-bold">CHIRINO STEEL</h1>
                    <h2 className="text-base font-semibold">ORDEN DE COMPRA N. {detalleOC.orden_compra?.numero_oc}</h2>
                  </div>
                  <div className="mb-4 p-3 bg-gray-50 rounded border print:bg-white print:border-black">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">N. OC</p>
                        <p className="font-bold text-blue-600">{detalleOC.orden_compra?.numero_oc}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">OT</p>
                        <p className="font-medium">{detalleOC.orden_compra?.ot_numero || detalleOC.orden_compra?.ot || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Fecha</p>
                        <p className="font-medium">{fmtFecha(detalleOC.orden_compra?.fecha)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Estado</p>
                        <p className={`font-medium ${detalleOC.orden_compra?.aprobada ? 'text-green-600' : 'text-orange-600'}`}>
                          {detalleOC.orden_compra?.aprobada ? 'Aprobada' : 'Pendiente'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Proveedor</p>
                          <p className="font-medium">{detalleOC.orden_compra?.nombre_proveedor || detalleOC.orden_compra?.rut_proveedor || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">RUT Proveedor</p>
                          <p className="font-mono text-sm">{detalleOC.orden_compra?.rut_proveedor || '-'}</p>
                        </div>
                      </div>
                      {detalleOC.orden_compra?.solicitante && (
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground">Solicitante</p>
                          <p className="font-medium">{detalleOC.orden_compra.solicitante}</p>
                        </div>
                      )}
                      {detalleOC.orden_compra?.observaciones && (
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground">Observaciones</p>
                          <p className="text-sm text-gray-600">{detalleOC.orden_compra.observaciones}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {detalleOC.detalles?.length > 0 ? (
                    <>
                      <h4 className="font-medium text-sm mb-2">Detalle de Items ({detalleOC.cantidad_lineas || detalleOC.detalles.length})</h4>
                      <table className="w-full text-xs border-collapse mb-4 print:border print:border-black">
                        <thead>
                          <tr className="bg-gray-200 print:bg-gray-100">
                            <th className="border border-gray-300 print:border-black p-1 text-center w-12">#</th>
                            <th className="border border-gray-300 print:border-black p-1 text-left">Descripcion</th>
                            <th className="border border-gray-300 print:border-black p-1 text-center w-20">Cantidad</th>
                            <th className="border border-gray-300 print:border-black p-1 text-right w-24">V. Unitario</th>
                            <th className="border border-gray-300 print:border-black p-1 text-right w-24">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalleOC.detalles.map((item, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50 print:bg-white'}>
                              <td className="border border-gray-300 print:border-black p-1 text-center">{idx + 1}</td>
                              <td className="border border-gray-300 print:border-black p-1">{item.descripcion || '-'}</td>
                              <td className="border border-gray-300 print:border-black p-1 text-center">{fmtCant(item.cantidad)}</td>
                              <td className="border border-gray-300 print:border-black p-1 text-right font-mono">${fmt(item.precio_unitario || item.valor_unitario)}</td>
                              <td className="border border-gray-300 print:border-black p-1 text-right font-mono">${fmt(item.total_linea || item.total_item || (item.cantidad * (item.precio_unitario || item.valor_unitario)))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No hay items detallados para esta OC</p>
                  )}
                  <div className="bg-gray-100 rounded p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Total Neto:</span>
                      <span className="text-lg font-bold text-green-600">${fmt(detalleOC.orden_compra?.neto || detalleOC.orden_compra?.valor_neto || detalleOC.orden_compra?.total)}</span>
                    </div>
                    {detalleOC.orden_compra?.total && detalleOC.orden_compra?.neto && detalleOC.orden_compra?.total !== detalleOC.orden_compra?.neto && (
                      <div className="flex justify-between items-center mt-1 text-sm text-muted-foreground">
                        <span>Total c/IVA:</span>
                        <span>${fmt(detalleOC.orden_compra?.total)}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-muted-foreground">No se encontro detalle para esta OC</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Initial State */}
      {!data && !loading && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Search className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-600">Ingrese un numero de OT</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Para ver el estado de pedidos, ordenes de compra y recepciones
          </p>
        </div>
      )}
    </div>
  );
};
